import { useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { loginByMagicToken } from '@realadvisor/auth-client';
import { Image } from '@realadvisor/image';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '../../../apollo/__generated__';

export const VALIDATE_INVITATION = gql(/* GraphQL */ `
  query ValidateInvitation($token: String!) {
    validate_invitation(token: $token) {
      success
      tenant_id
      magic_token
    }
  }
`);

export const InvitationAccept = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [isValidated, setIsValidated] = useState(false);
  const [error, setError] = useState('');

  const { loading } = useQuery(VALIDATE_INVITATION, {
    variables: token ? { token } : undefined,
    skip: !token,
    onCompleted: async data => {
      const result = data.validate_invitation;
      if (result?.success && result.magic_token && result.tenant_id) {
        setIsValidated(true);
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('token', result.magic_token);
        currentUrl.searchParams.set('tenant_id', result.tenant_id);
        window.history.replaceState({}, '', currentUrl.toString());

        await loginByMagicToken();
      } else {
        setError(
          t('Invalid invitation response. Please check your invitation link.'),
        );
      }
    },
    onError: () => {
      setError(t('Network error occurred while validating invitation.'));
    },
  });

  if (!token) {
    return (
      <Container
        maxWidth="sm"
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
      >
        <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
          <Box
            sx={{
              margin: '0 auto',
              mb: 3,
              width: 256,
            }}
          >
            <Image
              src="/realadvisor-logo.svg"
              alt="RealAdvisor Logo"
              options={{ w: 256 }}
            />
          </Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('No invitation token provided.')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('Please use the complete invitation link or contact support.')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
        <Box
          sx={{
            margin: '0 auto',
            mb: 4,
            width: 256,
          }}
        >
          <Image
            src="/realadvisor-logo.svg"
            alt="RealAdvisor Logo"
            options={{ w: 256 }}
          />
        </Box>

        {loading && (
          <Box>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('Just a moment...')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Processing your invitation')}
            </Typography>
          </Box>
        )}

        {error && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {t('Please check the link or try again.')}
            </Typography>
          </Box>
        )}

        {isValidated && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('Your account has been successfully activated.')}
            </Alert>
            <Typography variant="h6" gutterBottom>
              {t('Welcome aboard!')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('You are now ready to access your dashboard.')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                navigate({
                  pathname: '/',
                });
                window.location.reload();
              }}
              sx={{
                background: 'linear-gradient(135deg, #54beda 0%, #0a39b7 100%)',
                px: 4,
                py: 1.5,
                margin: '0 auto',
              }}
            >
              {t('Continue to Dashboard')}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default InvitationAccept;
